// API Service for Hotel La Rosa Negra
// Fetches data from MongoDB via FastAPI backend

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache for API responses to avoid repeated calls
const cache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

async function fetchWithCache(endpoint, options = {}) {
  const cacheKey = endpoint;
  const cached = cache.get(cacheKey);
  
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    
    const data = await response.json();
    cache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  } catch (error) {
    console.error(`Failed to fetch ${endpoint}:`, error);
    throw error;
  }
}

// Hotel Info
export async function getHotelInfo() {
  return fetchWithCache('/api/hotel-info');
}

// Rooms
export async function getRooms() {
  return fetchWithCache('/api/rooms');
}

export async function getRoom(roomId) {
  return fetchWithCache(`/api/rooms/${roomId}`);
}

// Gallery
export async function getGallery() {
  return fetchWithCache('/api/gallery');
}

// Amenities
export async function getAmenities() {
  return fetchWithCache('/api/amenities');
}

// Services
export async function getServices() {
  return fetchWithCache('/api/services');
}

// Testimonials
export async function getTestimonials() {
  return fetchWithCache('/api/testimonials');
}

// FAQs
export async function getFaqs() {
  return fetchWithCache('/api/faqs');
}

// Nearby Attractions
export async function getNearbyAttractions() {
  return fetchWithCache('/api/nearby-attractions');
}

// Beds24 Integration
export async function getAvailability(checkIn, checkOut, adults = 2, children = 0) {
  const params = new URLSearchParams({
    check_in: checkIn,
    check_out: checkOut,
    adults: adults.toString(),
    children: children.toString(),
  });
  return fetchWithCache(`/api/beds24/availability?${params}`);
}

export async function calculatePrice(checkIn, checkOut, adults = 2, children = 0) {
  const response = await fetch(`${API_BASE_URL}/api/beds24/calculate-price`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      check_in: checkIn,
      check_out: checkOut,
      adults,
      children,
    }),
  });
  
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  
  return response.json();
}

// Clear cache (useful for admin updates)
export function clearCache() {
  cache.clear();
}

// Analytics Tracking
export async function trackEvent(eventType, data = {}) {
  try {
    await fetch(`${API_BASE_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: eventType,
        page: data.page || window.location.pathname,
        room_id: data.room_id || null,
        metadata: data.metadata || null,
      }),
    });
  } catch (_error) {
    // Silently fail analytics - don't block user experience
  }
}

// Track page view
export function trackPageView(page) {
  trackEvent('page_view', { page });
}

// Track room view
export function trackRoomView(roomId) {
  trackEvent('room_view', { room_id: roomId });
}

// Track booking click
export function trackBookingClick(roomId) {
  trackEvent('booking_click', { room_id: roomId });
}

// Booking funnel tracking — de-duped within a single client session so a
// visitor toggling checkbox filters doesn't inflate the top of the funnel.
// Session key resets on hard reload, which is a reasonable "funnel start".
const _funnelFired = new Set();
export function trackFunnelStep(step, extra = {}) {
  if (!step) return;
  if (_funnelFired.has(step)) return;
  _funnelFired.add(step);
  trackEvent(step, extra);
}
// Convenience helpers used by Rooms.jsx / RoomDetail.jsx.
export const trackRoomsPageView = () => trackFunnelStep('rooms_page_view');
export const trackDatesSelected = () => trackFunnelStep('funnel_dates_selected');
export const trackAvailabilitySeen = () => trackFunnelStep('funnel_availability_seen');
export const trackBookModalOpened = (roomId) =>
  trackFunnelStep('funnel_book_modal_opened', { room_id: roomId });
export const trackBookConfirmed = (roomId) =>
  trackFunnelStep('funnel_book_confirmed', { room_id: roomId });

export default {
  getHotelInfo,
  getRooms,
  getRoom,
  getGallery,
  getAmenities,
  getServices,
  getTestimonials,
  getFaqs,
  getNearbyAttractions,
  getAvailability,
  calculatePrice,
  clearCache,
  trackEvent,
  trackPageView,
  trackRoomView,
  trackBookingClick,
  trackFunnelStep,
  trackRoomsPageView,
  trackDatesSelected,
  trackAvailabilitySeen,
  trackBookModalOpened,
  trackBookConfirmed,
};
